import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FeatureComparisonProps {
  section: any;
}

export default function FeatureComparison({ section }: FeatureComparisonProps) {
  const plans: any[] = section.content?.plans ?? [];
  const features: any[] = section.content?.features ?? [];
  const headingId = `feature-comparison-heading-${section.id}`;

  if (plans.length === 0 || features.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          {section.title && (
            <h2
              id={headingId}
              className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
            >
              {section.title}
            </h2>
          )}
          {section.subtitle && (
            <p className="mt-2 text-sage-600 max-w-xl mx-auto">
              {section.subtitle}
            </p>
          )}
        </div>

        {/* Table wrapper: horizontally scrollable on mobile */}
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full min-w-[600px] border-collapse">
            <thead>
              <tr>
                {/* Empty cell for feature name column */}
                <th
                  className="sticky left-0 z-10 bg-surface text-left p-4 border-b border-sage-200"
                  scope="col"
                >
                  <span className="sr-only">Feature</span>
                </th>
                {plans.map((plan: any, i: number) => (
                  <th
                    key={i}
                    scope="col"
                    className="p-4 text-center border-b border-sage-200"
                  >
                    <div className="font-display text-lg font-semibold text-sage-900">
                      {plan.name}
                    </div>
                    {plan.price != null && (
                      <div className="mt-1 text-sm text-sage-600">
                        <span className="font-display text-2xl font-bold text-sage-900">
                          {typeof plan.price === 'number'
                            ? `$${plan.price}`
                            : plan.price}
                        </span>
                        {plan.interval && (
                          <span className="text-sage-500">/{plan.interval}</span>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((feature: any, fi: number) => (
                <tr
                  key={fi}
                  className={cn(
                    fi % 2 === 0 ? 'bg-surface' : 'bg-sage-50/50'
                  )}
                >
                  <td className="sticky left-0 z-10 p-4 text-sm font-medium text-sage-800 border-b border-sage-100 bg-inherit">
                    {feature.name}
                  </td>
                  {(feature.included ?? []).map(
                    (included: boolean, pi: number) => (
                      <td
                        key={pi}
                        className="p-4 text-center border-b border-sage-100"
                      >
                        {included ? (
                          <Check
                            className="h-5 w-5 text-sage-600 mx-auto"
                            aria-label={`${feature.name} included in ${plans[pi]?.name}`}
                          />
                        ) : (
                          <X
                            className="h-5 w-5 text-sage-300 mx-auto"
                            aria-label={`${feature.name} not included in ${plans[pi]?.name}`}
                          />
                        )}
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
